import { Domain } from '../enums/domain';

/**
 * The German federal states (Bundesländer) and the subset of them that has a
 * Federal State Page of its own.
 *
 * Lives in `packages/shared` for the reason `../seo/canonicalOrigins` gives:
 * the website builds the `/{bundesland}/` route from this list and the
 * backend's sitemap generator has to emit exactly those URLs. A second slug
 * list in the backend would drift the moment a state moves between the two
 * groups, and the drift would only show up as a sitemap full of 404s.
 *
 * Authority: spec 10.02.07-Federal-State-Page § AC 1.
 */

export interface Region {
  id: string;
  name: string;
  slug: string;
  /**
   * This region has no Federal State Page and links to its City Location
   * Listing instead, so `/{slug}/` 404s for it.
   *
   * TBD: the criterion is not city-statehood (Saarland is not a city-state) but
   * an unconfirmed size threshold — hardcoded until Schülerhilfe confirms it.
   */
  usesCityListing?: boolean;
}

/**
 * All 16 states, not only the 12 that are routable: the location import tags
 * Standorte `bundesland:berlin` and friends, so a slug→name lookup restricted
 * to the routable states would fail on exactly those.
 */
export const GERMAN_REGIONS: readonly Region[] = [
  { id: 'DE-BW', name: 'Baden-Württemberg', slug: 'baden-wuerttemberg' },
  { id: 'DE-BY', name: 'Bayern', slug: 'bayern' },
  { id: 'DE-BE', name: 'Berlin', slug: 'berlin', usesCityListing: true },
  { id: 'DE-BB', name: 'Brandenburg', slug: 'brandenburg' },
  { id: 'DE-HB', name: 'Bremen', slug: 'bremen', usesCityListing: true },
  { id: 'DE-HH', name: 'Hamburg', slug: 'hamburg', usesCityListing: true },
  { id: 'DE-HE', name: 'Hessen', slug: 'hessen' },
  {
    id: 'DE-MV',
    name: 'Mecklenburg-Vorpommern',
    slug: 'mecklenburg-vorpommern',
  },
  { id: 'DE-NI', name: 'Niedersachsen', slug: 'niedersachsen' },
  { id: 'DE-NW', name: 'Nordrhein-Westfalen', slug: 'nordrhein-westfalen' },
  { id: 'DE-RP', name: 'Rheinland-Pfalz', slug: 'rheinland-pfalz' },
  { id: 'DE-SL', name: 'Saarland', slug: 'saarland', usesCityListing: true },
  { id: 'DE-SN', name: 'Sachsen', slug: 'sachsen' },
  { id: 'DE-ST', name: 'Sachsen-Anhalt', slug: 'sachsen-anhalt' },
  { id: 'DE-SH', name: 'Schleswig-Holstein', slug: 'schleswig-holstein' },
  { id: 'DE-TH', name: 'Thüringen', slug: 'thueringen' },
];

/**
 * The 12 slugs served at `/{slug}/`. Berlin, Bremen, Hamburg and Saarland are
 * absent: they are covered by City Pages under `/standorte/`, so their
 * `/{slug}/` URL 404s and must never enter a sitemap.
 */
export const BUNDESLAND_SLUGS: readonly string[] = GERMAN_REGIONS.filter(
  (region) => !region.usesCityListing,
).map((region) => region.slug);

/**
 * The display name of the German region a `bundesland:*` tag value names
 * (`brandenburg` → `Brandenburg`), or `undefined` for a value that names none.
 * Tag values are compared case-insensitively, like every tag lookup.
 */
export function bundeslandNameOf(slug: string): string | undefined {
  const needle = slug.toLowerCase();
  return GERMAN_REGIONS.find((region) => region.slug === needle)?.name;
}

/**
 * The brands that may serve `/{bundesland}/` at all.
 *
 * These twelve slugs are **German federal states**. Schülerhilfe .de and
 * erstenachhilfe both sell into Germany, so the URLs mean something on both;
 * `schuelerhilfe.at` and `fit4school.ch` are other countries, where a page
 * about Bayern is not merely untranslated but wrong.
 *
 * This is a geography check, not a brand-isolation one, which is why it is
 * hardcoded while the rest of {@link ../../../spec/70-Standards/70.15-Brand-Isolation.md}
 * pushes such decisions into the CMS. The CMS still decides **within** this
 * list: a `type:bundesland` template with no `domain:*` tag serves both German
 * brands, one tagged `domain:de` serves only `.de`. What an editor cannot do is
 * publish German state pages onto the Austrian or Swiss site, which no tagging
 * should ever express.
 *
 * Both the route (`isBundeslandSlug`) and the sitemap
 * (`BundeslandSitemapProvider`) read this, so a brand cannot serve a URL its
 * own sitemap omits — or the reverse.
 */
export const BUNDESLAND_ROUTE_DOMAINS: readonly Domain[] = [
  Domain.De,
  Domain.Erna,
];

/** Does this brand serve the German Federal State Pages at all? */
export function servesBundeslandRoutes(domain: Domain | undefined): boolean {
  return !!domain && BUNDESLAND_ROUTE_DOMAINS.includes(domain);
}
